import Icon from '@/componentsDS/Icon/Icon';
import InputCaption from '@/componentsDS/Input/InputCaption';
import InputLabel from '@/componentsDS/Input/InputLabel';
import Loader from '@/componentsDS/Loader/Loader';
import Tooltip from '@/componentsDS/Tooltip';
import { filterByLabel, SELECT_ALL_VALUE } from '@/utils/select-items';
import { Select, SelectContent, SelectItem } from '@dextech-ai/dex-design-system';
import PropTypes from 'prop-types';
import { useRef } from 'react';

// A design-system Select with the field chrome the old text-input picker had: label,
// required mark, label tooltip, error / info text and caption.
//
// The props are the old ones, so callers did not change:
// - options are `{ id, label, tooltipInfo? }`; `onSelectDropdownItem` gets the picked
//   option objects (an array, also for a single select).
// - `value` / `onChange` carry the text shown in the field. A multi select reports the
//   search text as it is typed ('' once it is used up). A single select reports the
//   label of the picked option ('' when cleared), and callers that keep no
//   `dropdownSelectedOption` (sign-up company info) pass that label back as `value`
//   to say which option is picked.
const InputDropdown = ({ id, name, label, labelInfoText, size, fullWidth = false, required, disabled, readOnly = false, infoText, errorMessage, captionText, value, onChange, dropdownOption, dropdownSelectedOption, onSelectDropdownItem, multipleSelection = false, allowSelectAll, isLoading, showTooltipInfo = false, placeholder, dataTestId }) => {
	const searchText = useRef('');

	const options = dropdownOption ?? [];
	// Callers prefill from a lookup that can miss (e.g. `[options.find(...)]`), so drop empty entries.
	const selectedOptions = (dropdownSelectedOption ?? []).filter(Boolean);
	// Picked options the list no longer holds still need their label for the chip.
	const knownOptions = [...options, ...selectedOptions.filter((selected) => !options.some((option) => option.id === selected.id))];
	const findOption = (optionId) => knownOptions.find((option) => option.id === optionId);

	const reportText = (text) => onChange?.(text);

	const clearSearchText = () => {
		if (searchText.current) {
			searchText.current = '';
			reportText('');
		}
	};

	const singleValue = () => {
		const picked = dropdownSelectedOption ? selectedOptions[0] : options.find((option) => value && option.label === value);
		// null, not undefined: rc-select reads undefined as uncontrolled and keeps the last pick.
		return picked && findOption(picked.id) ? picked.id : null;
	};

	const handleMultipleChange = (values) => {
		clearSearchText();

		if (values.includes(SELECT_ALL_VALUE)) {
			const allSelected = options.length > 0 && options.every((option) => selectedOptions.some((selected) => selected.id === option.id));
			onSelectDropdownItem(allSelected ? [] : options);
			return;
		}

		onSelectDropdownItem(values.map(findOption).filter(Boolean));
	};

	const handleSingleChange = (optionId) => {
		const picked = findOption(optionId);
		onSelectDropdownItem(picked ? [picked] : []);
		reportText(picked?.label ?? '');
	};

	const handleSearch = (text) => {
		if (multipleSelection) {
			searchText.current = text;
			reportText(text);
		}
	};

	const handleOpenChange = (open) => {
		if (!open && multipleSelection) {
			clearSearchText();
		}
	};

	// While the options load, the open list shows the old picker's spinner in place of the
	// items. It does this even when the list already holds options (fixed extras such as
	// "Invite New User", or the previous load), so a pick can't be made from a partial list.
	const popupRender = (menu) => (isLoading ? (
		<div className="DS__dropdown-list__loading-list">
			<Loader />
		</div>
	) : menu);

	const optionRender = (option) => {
		const item = findOption(option.value);
		if (!item?.tooltipInfo) {
			return option.label;
		}

		return (
			<span className="ds-select-option-with-info">
				<span className="ds-select-option-with-info__label">{option.label}</span>
				<Tooltip id={`dropdown-option-tooltip-${item.label}`} placement="bottom-end" render={() => item.tooltipInfo} portal>
					<Icon name="information-circle" className="card-configure__body__dropdown__with-icon-image" style="solid" size="small" />
				</Tooltip>
			</span>
		);
	};

	return (
		<div className="DS__input-dropdown" style={{ width: fullWidth ? '100%' : '350px' }}>
			{label && <InputLabel id={id} label={label} required={required} labelInfoText={labelInfoText} />}
			<Select
				id={id}
				data-testid={dataTestId ?? (name ? `${name}-select` : undefined)}
				aria-label={label ?? placeholder}
				multiple={multipleSelection}
				size={size === 'small' ? 'md' : 'lg'}
				placeholder={placeholder || 'Search'}
				value={multipleSelection ? selectedOptions.map((option) => option.id) : singleValue()}
				onChange={multipleSelection ? handleMultipleChange : handleSingleChange}
				onSearch={handleSearch}
				onOpenChange={handleOpenChange}
				showSearch={!readOnly}
				filterOption={filterByLabel}
				allowClear={!readOnly}
				maxTagCount={multipleSelection ? 'responsive' : undefined}
				disabled={disabled}
				invalid={Boolean(errorMessage)}
				loading={isLoading}
				notFoundContent="No results found."
				popupRender={popupRender}
				optionRender={showTooltipInfo ? optionRender : undefined}
			>
				<SelectContent>
					{multipleSelection && allowSelectAll && options.length > 0 && (
						<SelectItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>All options</SelectItem>
					)}
					{knownOptions.map((option) => (
						<SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
					))}
				</SelectContent>
			</Select>
			{(errorMessage || infoText || captionText) && (
				<InputCaption id={id} errorMessage={errorMessage} infoText={infoText} captionText={captionText} />
			)}
		</div>
	);
};

export default InputDropdown;

InputDropdown.propTypes = {
	id: PropTypes.string,
	name: PropTypes.string,
	label: PropTypes.string,
	labelInfoText: PropTypes.string,
	size: PropTypes.oneOf(['default', 'small']),
	fullWidth: PropTypes.bool,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	infoText: PropTypes.string,
	errorMessage: PropTypes.string,
	captionText: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	dropdownOption: PropTypes.array,
	dropdownSelectedOption: PropTypes.array,
	onSelectDropdownItem: PropTypes.func,
	multipleSelection: PropTypes.bool,
	allowSelectAll: PropTypes.bool,
	isLoading: PropTypes.bool,
	placeholder: PropTypes.string,
	showTooltipInfo: PropTypes.bool,
	dataTestId: PropTypes.string
};
